import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Grid, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Autocomplete,
  FormHelperText,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useForm, Controller, useFieldArray, FieldValues, Control } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import PageContainer from '../../components/layout/PageContainer';
import { StockTransactionType } from '../../types/inventory';
import { Item } from '../../types/item';
import { Location } from '../../types/location';

// Form field types
type StockTransferItem = {
  itemId: string;
  quantity: number;
  batchNumber: string;
  expiryDate: string;
};

type StockTransferFormValues = {
  fromLocationId: string;
  toLocationId: string;
  reference: string;
  notes: string;
  items: StockTransferItem[];
};

// Type for the form control
type StockTransferFormControl = Control<StockTransferFormValues, unknown>;

// Mock data - replace with API calls
const mockItems: Item[] = [
  { 
    id: '1', 
    itemCode: 'ITEM-001', 
    itemName: 'Paracetamol', 
    description: 'Pain reliever',
    price: 10.99,
    status: 'active',
    itemType: 'inventory',
    itemGroupId: '1',
    unitOfMeasureId: '1',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  { 
    id: '2', 
    itemCode: 'ITEM-002', 
    itemName: 'Ibuprofen', 
    description: 'Anti-inflammatory',
    price: 8.99,
    status: 'active',
    itemType: 'inventory',
    itemGroupId: '1',
    unitOfMeasureId: '1',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
];

const mockLocations: Location[] = [
  { 
    id: '1', 
    name: 'Main Pharmacy', 
    code: 'PH-001',
    locationType: 'Pharmacy',
    status: true,
    locationSubType: 'Dispensary',
    facilityId: '1',
    facilityName: 'Main Hospital',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  { 
    id: '2', 
    name: 'Emergency Ward', 
    code: 'WARD-ER',
    locationType: 'Ward',
    status: true,
    locationSubType: 'Emergency',
    facilityId: '1',
    facilityName: 'Main Hospital',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  { 
    id: '3', 
    name: 'OPD', 
    code: 'WARD-OPD',
    locationType: 'Ward',
    status: true,
    locationSubType: 'Outpatient',
    facilityId: '1',
    facilityName: 'Main Hospital',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
];

// Create a schema for individual items
const itemSchema = yup.object().shape({
  itemId: yup.string().required('Item is required'),
  quantity: yup
    .number()
    .typeError('Quantity must be a number')
    .required('Quantity is required')
    .min(1, 'Quantity must be at least 1'),
  batchNumber: yup.string().default(''),
  expiryDate: yup.string().default(''),
});

// Create the main schema
const schema = yup.object().shape({
  fromLocationId: yup.string().required('From Location is required'),
  toLocationId: yup.string()
    .required('To Location is required')
    .notOneOf([yup.ref('fromLocationId')], 'Must be different from From Location'),
  reference: yup.string().default(''),
  notes: yup.string().default(''),
  items: yup.array().of(itemSchema).min(1, 'At least one item is required'),
}) as yup.ObjectSchema<StockTransferFormValues>;

interface StockTransferFormData {
  fromLocationId: string;
  toLocationId: string;
  reference: string;
  notes: string;
  items: Array<{
    itemId: string;
    quantity: number;
    batchNumber: string;
    expiryDate: string;
  }>;
}

const StockTransferPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { 
    control, 
    handleSubmit, 
    formState: { errors }, 
    watch, 
    reset 
  } = useForm<StockTransferFormValues>({
    resolver: yupResolver(schema) as any, // Cast to any to bypass type issues with yup and react-hook-form
    defaultValues: {
      fromLocationId: '',
      toLocationId: '',
      reference: '',
      notes: '',
      items: [{ 
        itemId: '', 
        quantity: 1, 
        batchNumber: '', 
        expiryDate: '' 
      }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  // Helper function to find item by ID
  const findItemById = (id: string): Item | null => {
    return mockItems.find((item: Item) => item.id === id) || null;
  };
  
  // Type guard to check if value is an Item with the expected shape
  const isItem = (value: unknown): value is Item => {
    return (value as Item)?.id !== undefined && 
           (value as Item)?.itemCode !== undefined &&
           (value as Item)?.itemName !== undefined;
  };

  const fromLocationId = watch('fromLocationId');
  const toLocationId = watch('toLocationId');

  const onSubmit = async (data: StockTransferFormData) => {
    try {
      setIsSubmitting(true);
      // TODO: Replace with actual API call
      console.log('Submitting stock transfer:', {
        ...data,
        transactionType: StockTransactionType.TRANSFER_OUT,
      });
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      enqueueSnackbar('Stock transfer initiated successfully', { variant: 'success' });
      reset();
    } catch (error) {
      console.error('Error processing stock transfer:', error);
      enqueueSnackbar('Failed to process stock transfer', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addItemRow = () => {
    append({ itemId: '', quantity: 1, batchNumber: '', expiryDate: '' });
  };

  return (
    <PageContainer title="Stock Transfer">
      <Paper sx={{ p: 3, mt: 2 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name="fromLocationId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.fromLocationId}>
                    <InputLabel>From Location *</InputLabel>
                    <Select
                      {...field}
                      label="From Location *"
                      error={!!errors.fromLocationId}
                    >
                      {mockLocations.map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name} ({location.code})
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.fromLocationId && (
                      <FormHelperText>{errors.fromLocationId.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Controller
                name="toLocationId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.toLocationId}>
                    <InputLabel>To Location *</InputLabel>
                    <Select
                      {...field}
                      label="To Location *"
                      error={!!errors.toLocationId}
                      disabled={!fromLocationId}
                    >
                      {mockLocations
                        .filter(loc => loc.id !== fromLocationId)
                        .map((location) => (
                          <MenuItem key={location.id} value={location.id}>
                            {location.name} ({location.code})
                          </MenuItem>
                        ))}
                    </Select>
                    {errors.toLocationId && (
                      <FormHelperText>{errors.toLocationId.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Items to Transfer</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addItemRow}
                  disabled={!fromLocationId || !toLocationId}
                >
                  Add Item
                </Button>
              </Box>
              
              {fields.map((item, index) => (
                <Box key={item.id} sx={{ mb: 3, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                      <Controller
                        name={`items.${index}.itemId`}
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            options={mockItems}
                            getOptionLabel={(option) => {
                              if (!option) return '';
                              if (typeof option === 'string') return option;
                              return `${option.itemCode} - ${option.itemName}`;
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Item *"
                                error={!!errors.items?.[index]?.itemId}
                                helperText={errors.items?.[index]?.itemId?.message}
                              />
                            )}
                            onChange={(_: unknown, value: Item | null) => {
                              field.onChange(value?.id || '');
                            }}
                            value={field.value ? findItemById(field.value) : null}
                            isOptionEqualToValue={(option: Item, value: Item) => {
                              if (!option || !value) return false;
                              return option.id === value.id;
                            }}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={2}>
                      <Controller
                        name={`items.${index}.quantity`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            label="Quantity *"
                            fullWidth
                            inputProps={{ min: 1 }}
                            error={!!errors.items?.[index]?.quantity}
                            helperText={errors.items?.[index]?.quantity?.message}
                            onChange={(e) => field.onChange(Number(e.target.value) || 1)}
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={2}>
                      <Controller
                        name={`items.${index}.batchNumber`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Batch #"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={2}>
                      <Controller
                        name={`items.${index}.expiryDate`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="date"
                            label="Expiry Date"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={1} display="flex" alignItems="center">
                      {fields.length > 1 && (
                        <IconButton 
                          color="error" 
                          onClick={() => remove(index)}
                          size="large"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              ))}
              
              {fields.length === 0 && (
                <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                  No items added. Select locations and click "Add Item" to get started.
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <Controller
                name="reference"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Reference #"
                    fullWidth
                    margin="normal"
                    helperText="e.g., Transfer request #, Requisition #, etc."
                  />
                )}
              />
              
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Notes"
                    fullWidth
                    multiline
                    rows={3}
                    margin="normal"
                    placeholder="Any additional information about this transfer..."
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => reset()}
                disabled={isSubmitting}
              >
                Reset
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={isSubmitting || fields.length === 0}
              >
                {isSubmitting ? 'Processing...' : 'Initiate Transfer'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </PageContainer>
  );
};

export default StockTransferPage;
